import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainSection } from '@/components/layout/MainSection';
import { SegmentedControl } from '@/components/ui';
import { useLeagueDetailContext } from '@/layouts/LeagueDetailLayout/context';
import { useLeagueStandings } from '@/hooks/useLeagueStandings';
import type { StandingsGroup, StandingsRow } from '@/types/league';
import styles from '../LeagueDetailPage.module.scss';

/**
 * Qualification/relegation zone colouring.
 *
 * `description` comes verbatim from API-Football (e.g. "Promotion - Champions
 * League (Group Stage)", "Relegation - Championship"). Only the three families
 * we can recognise get a colour bar; anything else renders without one — never
 * invent a zone the source did not describe.
 *
 * The bar is always paired with the legend below the table (colour + text), so
 * colour is never the sole carrier of meaning (WCAG 1.4.1).
 */
const ZONE_COLORS: ReadonlyArray<{ match: RegExp; color: string }> = [
  { match: /champions league/i, color: 'var(--azure)' },
  { match: /europa/i, color: 'var(--orange)' },
  { match: /relegation/i, color: 'var(--red)' },
];

function zoneColor(description: string | null): string | null {
  if (!description) return null;
  const found = ZONE_COLORS.find(zone => zone.match.test(description));
  return found ? found.color : null;
}

/** Distinct coloured descriptions present in the payload, in table order. */
function collectLegend(groups: StandingsGroup[]): Array<{ description: string; color: string }> {
  const seen = new Map<string, string>();
  for (const group of groups) {
    for (const row of group.rows) {
      const color = zoneColor(row.description);
      if (color && row.description && !seen.has(row.description)) {
        seen.set(row.description, color);
      }
    }
  }
  return Array.from(seen, ([description, color]) => ({ description, color }));
}

/**
 * A competition played in several leagues of groups stores each table as
 * "League A - Group A". Captures the league id and the group's own name.
 */
const LEAGUE_OF_GROUPS = /^League (\S+) - (.+)$/;

interface StandingsLeague {
  id: string;
  groups: StandingsGroup[];
}

/**
 * The groups split by league, each group under its own name. Empty unless
 * every group names its league and there are at least two leagues - any other
 * payload is one flat list of tables.
 */
function splitLeagues(groups: StandingsGroup[]): StandingsLeague[] {
  const leagues: StandingsLeague[] = [];
  for (const group of groups) {
    const [, id, name] = LEAGUE_OF_GROUPS.exec(group.group) ?? [];
    if (!id || !name) return [];
    let league = leagues.find(item => item.id === id);
    if (!league) {
      league = { id, groups: [] };
      leagues.push(league);
    }
    league.groups.push({ ...group, group: name });
  }
  return leagues.length > 1 ? leagues : [];
}

/** Most recent results first is the API's own order; show the last 5 entries. */
const FORM_LENGTH = 5;

const FormStrip: React.FC<{ form: string | null }> = ({ form }) => {
  const { t } = useTranslation('league');
  if (!form) return null;

  const results = form.slice(-FORM_LENGTH).split('');
  const label = (result: string): string => {
    if (result === 'W') return t('league:formWin');
    if (result === 'D') return t('league:formDraw');
    if (result === 'L') return t('league:formLoss');
    return result;
  };

  return (
    <span className={styles.formStrip}>
      {results.map((result, index) => (
        <span
          key={`${result}-${index}`}
          className={`${styles.formDot} ${styles[`form${result}`] || ''}`}
          title={label(result)}
        >
          {result}
        </span>
      ))}
    </span>
  );
};

const StandingsTable: React.FC<{ group: StandingsGroup; showHeader: boolean }> = ({
  group,
  showHeader,
}) => {
  const { t } = useTranslation('league');

  return (
    <section className={styles.standingsGroup}>
      {showHeader && <h3 className={styles.groupTitle}>{group.group}</h3>}

      <table className={styles.standingsTable} data-testid="standings-table">
        <thead>
          <tr>
            <th className={styles.colRank} scope="col" title={t('league:colRankFull')}>
              {t('league:colRank')}
            </th>
            <th className={styles.colTeam} scope="col">{t('league:colTeam')}</th>
            <th className={styles.colNum} scope="col" title={t('league:colPlayedFull')}>
              {t('league:colPlayed')}
            </th>
            <th className={styles.colDesktop} scope="col" title={t('league:colWonFull')}>
              {t('league:colWon')}
            </th>
            <th className={styles.colDesktop} scope="col" title={t('league:colDrawnFull')}>
              {t('league:colDrawn')}
            </th>
            <th className={styles.colDesktop} scope="col" title={t('league:colLostFull')}>
              {t('league:colLost')}
            </th>
            <th className={styles.colDesktop} scope="col" title={t('league:colGoalsFull')}>
              {t('league:colGoals')}
            </th>
            <th className={styles.colMobile} scope="col" title={t('league:colGoalDiffFull')}>
              {t('league:colGoalDiff')}
            </th>
            <th className={styles.colNum} scope="col" title={t('league:colPointsFull')}>
              {t('league:colPoints')}
            </th>
            <th className={styles.colForm} scope="col" title={t('league:colFormFull')}>
              {t('league:colForm')}
            </th>
          </tr>
        </thead>
        <tbody>
          {group.rows.map((row: StandingsRow) => {
            const color = zoneColor(row.description);
            const diff = row.goalsDiff;
            return (
              <tr key={row.team.id} data-testid="standings-row">
                <td className={styles.colRank}>
                  <span
                    className={styles.rankCell}
                    style={color ? { borderInlineStartColor: color } : undefined}
                    title={row.description || undefined}
                  >
                    {row.rank}
                  </span>
                </td>
                <td className={styles.colTeam}>
                  <span className={styles.teamCell}>
                    {row.team.logoUrl && (
                      <img
                        src={row.team.logoUrl}
                        alt=""
                        width={20}
                        height={20}
                        decoding="async"
                      />
                    )}
                    <span className={styles.teamName}>{row.team.name}</span>
                  </span>
                </td>
                <td className={styles.colNum}>{row.played}</td>
                <td className={styles.colDesktop}>{row.won}</td>
                <td className={styles.colDesktop}>{row.drawn}</td>
                <td className={styles.colDesktop}>{row.lost}</td>
                <td className={styles.colDesktop}>{`${row.goalsFor}:${row.goalsAgainst}`}</td>
                <td className={styles.colMobile}>{diff > 0 ? `+${diff}` : diff}</td>
                <td className={`${styles.colNum} ${styles.pointsCell}`}>{row.points}</td>
                <td className={styles.colForm}>
                  <FormStrip form={row.form} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

/**
 * `/matches/:leagueId/standings` — official league table(s), one per API group
 * ("League" for a single-table competition, "Group A"… for group stages).
 *
 * Mobile (<768px) reduces to `# | team | P | GD | Pts` via CSS rather than
 * horizontal scroll — a nested h-scroll fights the tab swipe on iOS.
 */
const StandingsTab: React.FC = () => {
  const { league, leagueId } = useLeagueDetailContext();
  const { t } = useTranslation(['league']);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);

  const { data, isLoading, error } = useLeagueStandings(leagueId);

  const leagues = useMemo(() => splitLeagues(data?.groups ?? []), [data]);
  const activeLeague = leagues.find(item => item.id === selectedLeague) ?? leagues[0];
  // One league at a time when the competition has several; every table otherwise.
  const groups = useMemo(() => activeLeague?.groups ?? data?.groups ?? [], [activeLeague, data]);
  const legend = useMemo(() => collectLegend(groups), [groups]);

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingStandings')}</p>;
  } else if (isLoading) {
    body = null;
  } else if (groups.length === 0) {
    body = (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>{t('league:standingsNotAvailable')}</p>
      </div>
    );
  } else {
    body = (
      <div className={styles.standingsWrap}>
        {activeLeague && (
          <SegmentedControl
            options={leagues.map(({ id }) => ({
              value: id,
              label: id,
              testId: `standings-league-${id}`,
            }))}
            value={activeLeague.id}
            onChange={setSelectedLeague}
            ariaLabel={t('league:leagues')}
            stretch
          />
        )}

        {groups.map(group => (
          <StandingsTable key={group.group} group={group} showHeader={groups.length > 1} />
        ))}

        {legend.length > 0 && (
          <div className={styles.legend}>
            <h4 className={styles.groupTitle}>{t('league:standingsLegend')}</h4>
            <ul className={styles.legendList}>
              {legend.map(({ description, color }) => (
                <li key={description} className={styles.legendItem}>
                  <span className={styles.legendBar} style={{ backgroundColor: color }} />
                  <span>{description}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <MainSection
      subtitleHideOnMobile
      title={league.name}
      subtitle={t('league:standings')}
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
    >
      {body}
    </MainSection>
  );
};

export default StandingsTab;
